import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { globalAudioManager } from '../../services/globalAudioManager';
import { deviceManager } from '../../services/deviceManager';
import { Mic, Square } from 'lucide-react';
import QueryVisualizer from './QueryVisualizer';
import ReactMarkdown from 'react-markdown';

const VisualizerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CanvasWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  display: flex;

  canvas {
    border-radius: ${({ theme }) => theme.borderRadius.xl};
  }
`;

const QueryVisualizerWrapper = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Add new styled components for transcribed text display within the visualizer
const TranscribedTextOverlay = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${({ theme }) => theme.colors.primary[300]};
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  z-index: 100;
  width: 80%;
  max-width: 700px;
  max-height: 70vh;
  word-wrap: break-word;
  line-height: 1.6;
  letter-spacing: 0.3px;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  text-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.8),
    0 0 20px rgba(43, 147, 255, 0.5);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  padding-top: 60px;
  padding-bottom: 40px;
  
  // Hide scrollbar but keep scroll functionality
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  // Smooth scrolling
  scroll-behavior: smooth;
  
  // Markdown styling
  p {
    margin: 0 0 0.5em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    font-weight: 700;
    color: #FFD700;
    text-shadow: 0 0 12px rgba(255, 215, 0, 0.6);
  }
  
  em {
    font-style: italic;
  }
  
  code {
    background: rgba(0, 0, 0, 0.6);
    padding: 3px 8px;
    border-radius: 6px;
    border: 1px solid rgba(43, 147, 255, 0.3);
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
    font-size: 0.85em;
  }
  
  ul, ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
    text-align: left;
  }
  
  li {
    margin: 0.25em 0;
  }
  
  h1, h2, h3, h4, h5, h6 {
    color: #FFD700;
    font-weight: 700;
    text-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
    margin: 0.8em 0 0.4em 0;
  }
  
  // Allow clicks to pass through to the canvas below
  pointer-events: none;
`;

const TranscribedTextContent = styled.p`
  color: ${({ theme }) => theme.colors.primary[300]};
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0;
  word-wrap: break-word;
  line-height: 1.4;
  max-height: 200px;
  overflow-y: auto;
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  
  // Allow clicks to pass through to the canvas below
  pointer-events: none;
`;

const TranscribedTextLabel = styled.span`
  display: block;
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 1rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-weight: 500;
`;

const StatusLabel = styled(motion.div)`
  position: absolute;
  top: 4%;
  alighn-self: center;
  transform: translateX(-50%);
  color: ${({ theme }) => theme.colors.dark.textSecondary};
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  z-index: 10;
  pointer-events: none;
  background: ${({ theme }) => theme.colors.dark.surface}90;
  backdrop-filter: blur(8px);
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.dark.border}40;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  width: auto;
  min-width: fit-content;
  
  // Allow clicks to pass through to the canvas below
  pointer-events: none;
`;

const CenterButton = styled(motion.button)`
  position: absolute;
  top: 45.5%;
  transform: translate(-50%, -50%);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary[500]}, 
    ${({ theme }) => theme.colors.accent.purple}
  );
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  transition: all ${({ theme }) => theme.transitions.normal};
  white-space: nowrap;
  min-width: fit-content;
  width: auto;
  
  /* ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Platform-adaptive styling */
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    min-height: 48px; /* Touch-friendly minimum height */
  }
  
  /* Touch device optimizations */
  @media (hover: none) and (pointer: coarse) {
    font-size: 0.85rem;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
    min-height: 50px;
    touch-action: manipulation; /* Prevent zoom on double-tap */
    -webkit-tap-highlight-color: transparent; /* Remove tap highlight */
  }
  
  /* High DPI display optimizations */
  @media (-webkit-min-device-pixel-ratio: 2) {
    border: 0.5px solid rgba(255, 255, 255, 0.1);
  }
  
  &:hover {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
  
  &:active {
    transform: translate(-50%, -50%) scale(0.98);
  }
  
  /* Focus styles for accessibility */
  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary[500]};
    outline-offset: 2px;
  }
  
  /* Disabled state */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  /* Special styling for recording state */
  &.recording {
    background: linear-gradient(135deg, 
      ${({ theme }) => theme.colors.accent.red}, 
      ${({ theme }) => theme.colors.accent.orange}
    );
  }
`;

// Create a separate styled component for the query button with different positioning
const QueryButton = styled(CenterButton)`
  top: auto;
  bottom: 5%; // Position below the visualization
  transform: translateX(-50%);
  
  &:hover {
    transform: translateX(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateX(-50%) scale(0.98);
  }
`;

// Create circular particle texture
function createCircularTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext('2d');

  const gradient = context.createRadialGradient(16, 16, 0, 16, 16, 16);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.8)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

  context.fillStyle = gradient;
  context.fillRect(0, 0, 32, 32);

  return new THREE.CanvasTexture(canvas);
}

class ParticleVisualizer {
  constructor(container, isBackgroundListening, onEnableListening, platformConfig = null) {
    this.container = container;
    this.isBackgroundListening = isBackgroundListening;
    this.onEnableListening = onEnableListening;

    // ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Platform-adaptive configuration
    this.platformConfig = platformConfig || {
      particleCount: 1800,
      renderScale: 1.0,
      updateFrequency: 60,
      buttonDelay: 150,
      useTouch: false,
      lowPower: false
    };

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
    this.renderer = new THREE.WebGLRenderer({
      antialias: !this.platformConfig.lowPower, // Disable antialiasing on low-power devices
      alpha: true,
      powerPreference: this.platformConfig.lowPower ? 'low-power' : 'high-performance'
    });
    this.particles = null;

    // ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Platform-adaptive particle management
    this.baseParticleCount = this.platformConfig.particleCount;
    this.reducedParticleCount = Math.floor(this.platformConfig.particleCount * 0.25); // 25% when backgrounded
    this.particleCount = this.baseParticleCount;
    this.isWindowVisible = true;
    this.isWindowFocused = true;

    this.audioContext = null;
    this.analyser = null;
    this.microphone = null;
    this.dataArray = null;
    this.frequencyData = null;
    this.mouse = new THREE.Vector2();
    this.mouse3D = new THREE.Vector3();
    this.clock = new THREE.Clock();
    this.particlePositions = [];
    this.particleVelocities = [];
    this.particleOpacities = [];
    this.particleStates = [];
    this.particleBasePositions = [];
    this.originalPositions = [];
    this.randomDirections = [];
    this.colors = [];
    this.baseRadius = 120;
    this.currentStream = null;  // Track current audio stream
    this.currentDeviceId = null;  // Track current device
    this.isSettingUpAudio = false;  // Prevent multiple simultaneous setups
    this.setupId = Math.random().toString(36);  // Unique setup identifier
    this.disposed = false;  // Track disposal state for persistent animation
    this.animationId = null;  // ⚠️ MEMORY LEAK FIX: Track animation frame ID for cleanup

    // Device switching and event handling - now using DeviceManager
    this.selectedDeviceId = 'default';  // Track selected device
    this.deviceSwitchHandlers = [];     // Track device switch cleanup functions
    this.autoDeviceSwitching = true;    // Enable automatic device switching
    this.animationPaused = false;
    this.lastFrameTime = 0;
    this.targetFPS = this.platformConfig.updateFrequency;
    this.reducedFPS = Math.floor(this.platformConfig.updateFrequency * 0.5); // Half FPS when backgrounded
    this.currentFPS = this.targetFPS;

    // ⚠️ THREE.js RESOURCE TRACKING for complete disposal
    this.threejsResources = {
      geometries: new Set(),
      materials: new Set(),
      textures: new Set(),
      bufferAttributes: new Set()
    };

    // Audio-reactive properties
    this.audioLevel = 0;
    this.smoothedAudioLevel = 0;
    this.voiceActivityLevel = 0;
    this.isVoiceActive = false;
    this.voiceThreshold = 0.008;  // More sensitive (reduced from 0.015)
    this.silenceThreshold = 0.001;  // More sensitive (reduced from 0.002)
    this.audioEnabled = false;

    // Visual properties - Balanced expansion and contraction
    this.sphereExpansion = 0;
    this.targetExpansion = 0;
    this.expansionSpeed = 0.25;  // Faster response for expansion
    this.contractionSpeed = 0.35;  // Even faster contraction
    this.maxExpansion = 60;  // Larger expansion (increased from 40)
    this.particleScatter = 0;
    this.targetScatter = 0;
    this.scatterSpeed = 0.18;  // Faster scatter response
    this.scatterContractionSpeed = 0.28;  // Faster scatter contraction
    this.maxScatter = 80;  // More scatter (increased from 60)

    // Rotation controls
    this.isMouseDown = false;
    this.previousMousePosition = { x: 0, y: 0 };
    this.targetQuaternion = new THREE.Quaternion();
    this.currentQuaternion = new THREE.Quaternion();
    this.sceneGroup = new THREE.Group();
    this.rotationSpeed = 0.005;
    this.rotationDamping = 0.1;

    this.init();
  }

  init() {
    // Platform-adaptive renderer setup with pixel ratio limiting
    const pixelRatio = Math.min(window.devicePixelRatio || 1, this.platformConfig.renderScale * 2);
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setClearColor(0x000000, 0);
    this.container.appendChild(this.renderer.domElement);

    // Setup camera
    this.camera.position.z = 300;

    // Add scene group
    this.scene.add(this.sceneGroup);
    this.currentQuaternion.copy(this.sceneGroup.quaternion);
    this.targetQuaternion.copy(this.sceneGroup.quaternion);

    // Create particles
    this.createParticles();

    // Setup audio if background listening is enabled
    if (this.isBackgroundListening) {
      this.setupAudio();
    }

    // Setup automatic device switching using DeviceManager
    this.setupDeviceSwitching();

    // ⚠️ GLOBAL EVENT LISTENER FIX: Bind handlers for proper cleanup
    this.boundMicrophoneChanged = (event) => {
      this.switchMicrophone(event.detail.deviceId);
    };

    // ⚠️ PARTICLE OPTIMIZATION: Bind visibility change handlers
    this.boundVisibilityChange = () => {
      this.handleVisibilityChange();
    };
    this.boundWindowBlur = () => {
      this.handleWindowBlur();
    };
    this.boundWindowFocus = () => {
      this.handleWindowFocus();
    };
    this.boundAppSwitched = (data) => {
      this.handleAppSwitched(data);
    };

    // ⚠️ GLOBAL EVENT LISTENER FIX: Add listeners with bound functions
    window.addEventListener('microphoneChanged', this.boundMicrophoneChanged);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    window.addEventListener('blur', this.boundWindowBlur);
    window.addEventListener('focus', this.boundWindowFocus);

    // Listen for app switching events from globalAudioManager
    if (globalAudioManager) {
      globalAudioManager.on('appSwitched', this.boundAppSwitched);
    }

    // Event listeners
    this.setupEventListeners();

    // Start animation
    this.animate();
  }

  createParticles() {
    const geometry = new THREE.BufferGeometry();
    this.particlePositions = new Float32Array(this.particleCount * 3);
    this.colors = new Float32Array(this.particleCount * 3);

    for (let i = 0; i < this.particleCount; i++) {
      const position = this.generateRandomSpherePosition();

      this.particlePositions[i * 3] = position.x;
      this.particlePositions[i * 3 + 1] = position.y;
      this.particlePositions[i * 3 + 2] = position.z;

      this.originalPositions.push(position.clone());
      this.particleBasePositions.push(position.clone());
      this.particleVelocities.push(new THREE.Vector3(0, 0, 0));
      this.particleOpacities.push(1.0);
      this.particleStates.push('alive');

      this.randomDirections.push(new THREE.Vector3(
        (Math.random() - 0.5),
        (Math.random() - 0.5),
        (Math.random() - 0.5)
      ).normalize());

      // Theme-based primary color (#2b93ff)
      this.colors[i * 3] = 0.169;     // R: 43/255
      this.colors[i * 3 + 1] = 0.576; // G: 147/255
      this.colors[i * 3 + 2] = 1.0;   // B: 255/255
    }

    // ⚠️ THREE.js RESOURCE TRACKING: Track BufferAttributes for disposal
    const positionAttribute = new THREE.BufferAttribute(this.particlePositions, 3);
    const colorAttribute = new THREE.BufferAttribute(this.colors, 3);

    geometry.setAttribute('position', positionAttribute);
    geometry.setAttribute('color', colorAttribute);

    // Track resources for complete disposal
    this.threejsResources.geometries.add(geometry);
    this.threejsResources.bufferAttributes.add(positionAttribute);
    this.threejsResources.bufferAttributes.add(colorAttribute);

    const circularTexture = createCircularTexture();
    // ⚠️ THREE.js RESOURCE TRACKING: Track texture for disposal
    this.threejsResources.textures.add(circularTexture);

    const material = new THREE.PointsMaterial({
      size: 2.2,
      map: circularTexture,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.8,
      sizeAttenuation: true,
      alphaTest: 0.01
    });

    // ⚠️ THREE.js RESOURCE TRACKING: Track material for disposal
    this.threejsResources.materials.add(material);

    this.particles = new THREE.Points(geometry, material);
    this.sceneGroup.add(this.particles);
  }

  generateRandomSpherePosition() {
    const phi = Math.random() * Math.PI * 2;
    const costheta = Math.random() * 2 - 1;
    const theta = Math.acos(costheta);

    const radiusVariation = (Math.random() - 0.5) * 20;
    const radius = this.baseRadius + radiusVariation;

    const x = radius * Math.sin(theta) * Math.cos(phi);
    const y = radius * Math.sin(theta) * Math.sin(phi);
    const z = radius * Math.cos(theta);

    return new THREE.Vector3(x, y, z);
  }

  async setupAudio(deviceId = null) {
    // Prevent multiple simultaneous setups
    if (this.isSettingUpAudio) {
      console.log(`🎤 Audio setup already in progress for ${this.setupId}`);
      return;
    }

    this.isSettingUpAudio = true;

    try {
      console.log(`🎤 Setting up audio visualization for ${this.setupId}...`);

      // Clean up existing audio resources first
      if (this.microphone) {
        this.microphone.disconnect();
        this.microphone = null;
      }

      if (this.currentStream && this.currentStream !== deviceManager.getAudioStream()) {
        this.currentStream.getTracks().forEach(track => track.stop());
      }

      // Get audio stream from DeviceManager
      let stream;
      if (deviceManager.isInitialized && !deviceId) {
        stream = deviceManager.getAudioStream();
      } else {
        // Use DeviceManager to set the device and get the stream
        if (deviceId) {
          await deviceManager.setInputDevice(deviceId);
        }
        stream = deviceManager.getAudioStream();
      }

      // ⚠️ WEB DEMO FALLBACK: If deviceManager failed to get a stream (common in browser), force it manually
      if (!stream && !window.electronAPI) {
        console.log('🎤 Web Demo: DeviceManager has no stream, forcing local getUserMedia...');
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }
          });
          // Manually inject this stream into deviceManager so other components can use it
          deviceManager.audioStream = stream;
          // Also make sure AudioContext is running
          if (deviceManager.audioContext && deviceManager.audioContext.state === 'suspended') {
            await deviceManager.audioContext.resume();
          }
        } catch (err) {
          console.error("❌ Web Demo: Failed to force get audio stream:", err);
        }
      }

      this.currentStream = stream;
      this.currentDeviceId = deviceId;
      this.selectedDeviceId = deviceId || 'default'; // Track selected device

      // Log microphone being used
      const track = stream.getAudioTracks()[0];
      if (track) {
        console.log(`🎤 Using microphone (${this.setupId}):`, track.label);
        // Trigger event to update UI with actual device being used
        window.dispatchEvent(new CustomEvent('actualMicrophoneChanged', {
          detail: {
            deviceId: track.getSettings().deviceId || deviceId,
            label: track.label
          }
        }));
      }

      // Get audio context from DeviceManager
      this.audioContext = deviceManager.getAudioContext();

      // ✅ FIX: If AudioContext is null on first load, create it explicitly
      if (!this.audioContext) {
        try {
          await deviceManager.createAudioContext();
          this.audioContext = deviceManager.getAudioContext();

          // Also setup audio processing (analyser)
          if (this.audioContext && stream) {
            await deviceManager.setupAudioProcessing();
          }
        } catch (err) {
          console.error(`❌ Failed to create AudioContext for ${this.setupId}:`, err);
        }
      }

      if (!this.audioContext || this.audioContext.state === 'closed') {
        throw new Error('AudioContext is closed or unavailable');
      }

      if (this.audioContext.state === 'suspended') {
        console.log('🎤 AudioContext suspended, attempting to resume...');
        try {
          await this.audioContext.resume();
          console.log('✅ AudioContext resumed!');
        } catch (e) {
          console.warn('⚠️ Could not resume AudioContext (waiting for user gesture)', e);
        }
      }

      // Get analyser from DeviceManager
      this.analyser = deviceManager.getAnalyser();

      // Create audio nodes only if context is running

      if (this.audioContext.state === 'running') {
        // Connect microphone to analyser if not already connected
        if (!this.microphone) {
          this.microphone = this.audioContext.createMediaStreamSource(stream);
          if (this.analyser) {
            this.microphone.connect(this.analyser);
          }
        }

        this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
        this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);

        this.audioEnabled = true;
        console.log(`✅ Audio visualization setup completed for ${this.setupId}`);
      } else {
        console.error(`❌ AudioContext state is ${this.audioContext.state}, cannot create audio nodes for ${this.setupId}`);
        this.audioEnabled = false;

        // Try to resume suspended AudioContext
        if (this.audioContext.state === 'suspended') {
          this.audioContext.resume().then(() => {
            // Retry setup after resume
            setTimeout(() => {
              this.isSettingUpAudio = false;
              this.setupAudio(deviceId);
            }, 100);
          }).catch(err => {
            console.error(`❌ Failed to resume AudioContext for ${this.setupId}:`, err);
          });
        }
      }
    } catch (error) {
      console.error(`❌ Audio setup failed for ${this.setupId}:`, error);
      this.audioEnabled = false;

      // If specific device failed, try default
      if (deviceId && deviceId !== 'default') {
        console.log(`🎤 Falling back to default microphone for ${this.setupId}`);
        setTimeout(() => {
          this.isSettingUpAudio = false;
          this.setupAudio('default');
        }, 500);
        return;
      }
    } finally {
      this.isSettingUpAudio = false;
    }
  }

  processAudioData() {
    // Only process audio if background listening is enabled OR if we are in demo mode and audio is enabled
    // In web demo, we want the visualizer to work even if "background listening" (Electron feature) is technically off
    const isDemoMode = !window.electronAPI;
    if (!this.isBackgroundListening && !isDemoMode) {
      // When background listening is disabled, reset all audio levels to 0
      this.audioLevel = 0;
      this.smoothedAudioLevel = 0;
      this.voiceActivityLevel = 0;
      this.isVoiceActive = false;
      // Reset visual effects to maintain static appearance
      this.targetExpansion = 0;
      this.targetScatter = 0;
      return;
    }

    // Continue audio processing when background listening is enabled
    if (!this.audioEnabled || !this.analyser || !this.dataArray) {
      // If audio is disabled, gradually reduce levels but don't reset state immediately
      // This maintains visual continuity during app switches
      this.audioLevel *= 0.95;
      this.smoothedAudioLevel *= 0.95;
      this.voiceActivityLevel *= 0.95;
      this.isVoiceActive = this.voiceActivityLevel > this.voiceThreshold;
      return;
    }

    try {
      this.analyser.getByteFrequencyData(this.frequencyData);
      this.analyser.getByteTimeDomainData(this.dataArray);

      // Calculate RMS for voice activity
      let rms = 0;
      for (let i = 0; i < this.dataArray.length; i++) {
        const normalized = (this.dataArray[i] - 128) / 128;
        rms += normalized * normalized;
      }
      rms = Math.sqrt(rms / this.dataArray.length);

      // Frequency analysis
      const bandSize = Math.floor(this.frequencyData.length / 3);
      let midSum = 0;
      for (let i = bandSize; i < bandSize * 2; i++) {
        midSum += this.frequencyData[i];
      }
      const midLevel = (midSum / bandSize) / 255;

      this.audioLevel = Math.max(rms, midLevel);
      this.voiceActivityLevel = Math.max(rms, midLevel);

      if (this.audioLevel < this.silenceThreshold) {
        this.audioLevel = 0;
        this.voiceActivityLevel = 0;
      }

      this.isVoiceActive = this.voiceActivityLevel > this.voiceThreshold;
      this.smoothedAudioLevel = this.smoothedAudioLevel * 0.65 + this.audioLevel * 0.35;  // Even more responsive smoothing

      // Update visual effects with exponential response for more dramatic effect
      const audioBoost = Math.pow(this.smoothedAudioLevel, 1.3);  // Exponential audio response
      const voiceBoost = this.isVoiceActive ? Math.pow(this.voiceActivityLevel, 1.2) : 0;

      this.targetExpansion = audioBoost * this.maxExpansion * 1.5;  // Increased multiplier
      this.targetScatter = audioBoost * this.maxScatter * 1.3;  // Increased multiplier

      if (this.isVoiceActive) {
        this.targetScatter += voiceBoost * this.maxScatter * 1.8;  // Enhanced voice response
        this.targetExpansion += voiceBoost * this.maxExpansion * 1.2;  // Added expansion on voice
      }
    } catch (error) {
      console.error('Error processing audio:', error);
    }
  }

  setupEventListeners() {
    // ⚠️ SAFETY CHECK: Only add listeners if renderer and domElement exist
    if (!this.renderer || !this.renderer.domElement) {
      return;
    }

    const canvas = this.renderer.domElement;

    // Bind event handlers first to ensure proper cleanup
    this.boundMouseDown = (e) => this.onMouseDown(e);
    this.boundMouseUp = (e) => this.onMouseUp(e);
    this.boundMouseMove = (e) => this.onMouseMove(e);
    this.boundTouchStart = (e) => this.onTouchStart(e);
    this.boundTouchMove = (e) => this.onTouchMove(e);
    this.boundTouchEnd = (e) => this.onTouchEnd(e);
    this.boundWindowResize = () => this.onWindowResize();

    // Add event listeners with bound functions
    canvas.addEventListener('mousedown', this.boundMouseDown);
    canvas.addEventListener('mouseup', this.boundMouseUp);
    canvas.addEventListener('mousemove', this.boundMouseMove);
    canvas.addEventListener('touchstart', this.boundTouchStart);
    canvas.addEventListener('touchmove', this.boundTouchMove);
    canvas.addEventListener('touchend', this.boundTouchEnd);

    window.addEventListener('resize', this.boundWindowResize);
  }

  onMouseDown(event) {
    if (event.button === 0) {
      this.isMouseDown = true;
      this.previousMousePosition = {
        x: event.clientX,
        y: event.clientY
      };
    }
  }

  onMouseUp(event) {
    if (event.button === 0) {
      this.isMouseDown = false;
    }
  }

  onMouseMove(event) {
    // ⚠️ SAFETY CHECK: Prevent null access after disposal
    if (!this.renderer || !this.renderer.domElement || this.disposed) {
      return;
    }

    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    if (this.isMouseDown) {
      const deltaMove = {
        x: event.clientX - this.previousMousePosition.x,
        y: event.clientY - this.previousMousePosition.y
      };

      const rotationX = new THREE.Quaternion();
      const rotationY = new THREE.Quaternion();
      const cameraRight = new THREE.Vector3(1, 0, 0);
      const cameraUp = new THREE.Vector3(0, 1, 0);

      rotationX.setFromAxisAngle(cameraUp, deltaMove.x * this.rotationSpeed);
      rotationY.setFromAxisAngle(cameraRight, deltaMove.y * this.rotationSpeed);

      const combinedRotation = new THREE.Quaternion();
      combinedRotation.multiplyQuaternions(rotationX, rotationY);
      this.targetQuaternion.multiplyQuaternions(combinedRotation, this.targetQuaternion);

      this.previousMousePosition = {
        x: event.clientX,
        y: event.clientY
      };
    }
  }

  onTouchStart(event) {
    if (event.touches.length === 1) {
      this.isMouseDown = true;
      const touch = event.touches[0];
      this.previousMousePosition = {
        x: touch.clientX,
        y: touch.clientY
      };
      event.preventDefault();
    }
  }

  onTouchMove(event) {
    // ⚠️ SAFETY CHECK: Prevent null access after disposal
    if (!this.renderer || !this.renderer.domElement || this.disposed || event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;

    if (this.isMouseDown) {
      const deltaMove = {
        x: touch.clientX - this.previousMousePosition.x,
        y: touch.clientY - this.previousMousePosition.y
      };

      const rotationX = new THREE.Quaternion();
      const rotationY = new THREE.Quaternion();
      const cameraRight = new THREE.Vector3(1, 0, 0);
      const cameraUp = new THREE.Vector3(0, 1, 0);

      rotationX.setFromAxisAngle(cameraUp, deltaMove.x * this.rotationSpeed);
      rotationY.setFromAxisAngle(cameraRight, deltaMove.y * this.rotationSpeed);

      const combinedRotation = new THREE.Quaternion();
      combinedRotation.multiplyQuaternions(rotationX, rotationY);
      this.targetQuaternion.multiplyQuaternions(combinedRotation, this.targetQuaternion);

      this.previousMousePosition = {
        x: touch.clientX,
        y: touch.clientY
      };
    }
    event.preventDefault();
  }

  onTouchEnd(event) {
    this.isMouseDown = false;
    event.preventDefault();
  }

  onWindowResize() {
    // ⚠️ SAFETY CHECK: Prevent null access after disposal
    if (!this.renderer || !this.camera || !this.container || this.disposed) {
      return;
    }

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }

  updateParticles() {
    const time = this.clock.getElapsedTime();
    const positions = this.particles.geometry.attributes.position.array;
    const colors = this.particles.geometry.attributes.color.array;

    this.processAudioData();

    // Update visual effects with dynamic contraction speeds - only when background listening is enabled
    if (this.isBackgroundListening) {
      const isExpanding = this.targetExpansion > this.sphereExpansion;
      const currentExpansionSpeed = isExpanding ? this.expansionSpeed : this.contractionSpeed;
      const isScattering = this.targetScatter > this.particleScatter;
      const currentScatterSpeed = isScattering ? this.scatterSpeed : this.scatterContractionSpeed;

      this.sphereExpansion = this.sphereExpansion * (1 - currentExpansionSpeed) + this.targetExpansion * currentExpansionSpeed;
      this.particleScatter = this.particleScatter * (1 - currentScatterSpeed) + this.targetScatter * currentScatterSpeed;
    } else {
      // When background listening is disabled, smoothly return to base state
      this.sphereExpansion *= 0.95;
      this.particleScatter *= 0.95;
    }

    // Update mouse 3D position for hover effects
    if (!this.isMouseDown && this.isBackgroundListening) {
      const vector = new THREE.Vector3(this.mouse.x, this.mouse.y, 0.5);
      vector.unproject(this.camera);
      const dir = vector.sub(this.camera.position).normalize();
      const distance = -this.camera.position.z / dir.z;
      this.mouse3D = this.camera.position.clone().add(dir.multiplyScalar(distance));
      this.mouse3D.applyQuaternion(this.sceneGroup.quaternion.clone().invert());
    }

    for (let i = 0; i < this.particleCount; i++) {
      const idx = i * 3;
      const particlePos = new THREE.Vector3(
        positions[idx],
        positions[idx + 1],
        positions[idx + 2]
      );

      const force = new THREE.Vector3();

      // Mouse hover effects (only when background listening is enabled)
      if (this.isBackgroundListening && !this.isMouseDown) {
        const distToMouse = particlePos.distanceTo(this.mouse3D);
        const mouseInfluence = Math.max(0, 1 - distToMouse / 150);

        if (mouseInfluence > 0) {
          const toMouse = this.mouse3D.clone().sub(particlePos);
          toMouse.normalize();
          const attractForce = toMouse.multiplyScalar(mouseInfluence * mouseInfluence * 1.5);
          force.add(attractForce);
        }
      }

      // Audio-reactive effects (only when background listening is enabled)
      if (this.isBackgroundListening && this.audioLevel > this.silenceThreshold) {
        const direction = particlePos.clone().normalize();

        // Enhanced wave effects - more responsive and dynamic
        const wavePhase = time * 5 + (particlePos.length() / this.baseRadius) * 10;  // Faster waves
        const waveEffect = Math.sin(wavePhase) * this.smoothedAudioLevel * 20;  // Reduced intensity for balance
        const waveForce = direction.clone().multiplyScalar(waveEffect * 0.03);  // Balanced force
        force.add(waveForce);

        // Balanced expansion effects
        const expansionForce = direction.clone().multiplyScalar(this.sphereExpansion * 0.05);
        force.add(expansionForce);

        // Balanced scatter effects
        const scatterForce = direction.clone().multiplyScalar(this.particleScatter * 0.07);
        force.add(scatterForce);

        // Enhanced voice-specific effects with better balance
        if (this.isVoiceActive) {
          const voiceShake = new THREE.Vector3(
            (Math.random() - 0.5) * this.voiceActivityLevel * 8,  // Reduced from 10 for balance
            (Math.random() - 0.5) * this.voiceActivityLevel * 8,
            (Math.random() - 0.5) * this.voiceActivityLevel * 8
          );
          force.add(voiceShake);

          // Add pulsing effect for voice activity with better timing
          const voicePulse = Math.sin(time * 10) * this.voiceActivityLevel * 0.4;  // Faster pulse, less intensity
          const pulseForce = direction.clone().multiplyScalar(voicePulse);
          force.add(pulseForce);
        }
      }

      // Return to sphere surface
      const currentDistance = particlePos.length();
      const targetRadius = this.baseRadius + this.sphereExpansion + this.particleScatter;

      if (Math.abs(currentDistance - targetRadius) > 10) {
        const idealPosition = this.particleBasePositions[i].clone().normalize();
        idealPosition.multiplyScalar(targetRadius);
        const returnVector = idealPosition.sub(particlePos);
        force.add(returnVector.multiplyScalar(0.02));
      }

      // Gentle movement for static mode
      if (!this.isBackgroundListening) {
        const gentleMovement = new THREE.Vector3(
          Math.sin(time * 0.5 + i * 0.01) * 0.3,
          Math.cos(time * 0.3 + i * 0.015) * 0.3,
          Math.sin(time * 0.4 + i * 0.02) * 0.3
        );
        force.add(gentleMovement);
      }

      // Brownian motion
      if (Math.random() < 0.01) {
        this.randomDirections[i] = new THREE.Vector3(
          (Math.random() - 0.5),
          (Math.random() - 0.5),
          (Math.random() - 0.5)
        ).normalize();
      }

      const brownianForce = this.randomDirections[i].clone().multiplyScalar(0.1);
      force.add(brownianForce);

      // Update velocity and position with balanced sensitivity
      this.particleVelocities[i].add(force.multiplyScalar(0.04));  // Balanced force application
      this.particleVelocities[i].multiplyScalar(0.94);  // Balanced damping for natural feel

      const maxSpeed = this.isBackgroundListening ? 2.5 + this.audioLevel * 2.0 : 1.0;  // Balanced max speed
      const speed = this.particleVelocities[i].length();
      if (speed > maxSpeed) {
        this.particleVelocities[i].normalize().multiplyScalar(maxSpeed);
      }

      positions[idx] += this.particleVelocities[i].x;
      positions[idx + 1] += this.particleVelocities[i].y;
      positions[idx + 2] += this.particleVelocities[i].z;

      // Enhanced color and brightness responsiveness
      let brightness = 0.7;

      if (this.isBackgroundListening) {
        const activity = this.particleVelocities[i].length() + this.smoothedAudioLevel * 3;  // Increased multiplier
        brightness = 0.7 + Math.min(0.4, activity * 0.15);  // Increased max brightness and sensitivity

        if (this.audioLevel > this.silenceThreshold) {
          brightness *= (1 + this.smoothedAudioLevel * 0.8);  // Increased audio brightness response
          const pulse = Math.sin(time * 3 + i * 0.015) * 0.08 + 1;  // Faster, more noticeable pulse
          brightness *= pulse;
        }

        if (this.isVoiceActive) {
          brightness *= (1 + this.voiceActivityLevel * 1.0);  // Increased voice brightness response
          // Add extra glow for active voice
          const voiceGlow = Math.sin(time * 8) * 0.3 + 1;
          brightness *= voiceGlow;
        }
      }

      // Apply theme color with brightness
      colors[idx] = 0.169 * brightness;
      colors[idx + 1] = 0.576 * brightness;
      colors[idx + 2] = 1.0 * brightness;
    }

    this.particles.geometry.attributes.position.needsUpdate = true;
    this.particles.geometry.attributes.color.needsUpdate = true;

    // Update rotation
    this.currentQuaternion.slerp(this.targetQuaternion, this.rotationDamping);
    this.sceneGroup.quaternion.copy(this.currentQuaternion);
  }

  updateBackgroundListening(enabled) {
    console.log(`🎤 Updating background listening for ${this.setupId}:`, enabled);
    this.isBackgroundListening = enabled;

    if (enabled) {
      // Always ensure audio is set up when enabling, even if it was previously enabled
      console.log(`🎤 Starting audio setup for ${this.setupId}`);
      this.setupAudio();
    }
  }

  // ⚠️ PARTICLE OPTIMIZATION: Window visibility management
  handleVisibilityChange() {
    this.isWindowVisible = !document.hidden;
    console.log(`📱 Window visibility changed: ${this.isWindowVisible ? 'visible' : 'hidden'}`);

    // ⚠️ NAVIGATION FIX: Never pause animation for navigation
    // Only optimize performance, but keep animation running
    this.optimizeParticleSystem();

    // ⚠️ NAVIGATION FIX: Force animation to continue
    this.animationPaused = false;

    // Restart animation if it was stopped
    if (!this.animationId && !this.disposed) {
      this.animate();
      console.log('🎬 Restarted animation after visibility change');
    }
  }

  handleWindowBlur() {
    this.isWindowFocused = false;
    console.log('📱 Window lost focus - optimizing particle system');

    // ⚠️ NAVIGATION FIX: Never pause animation, just optimize
    this.animationPaused = false;
    this.optimizeParticleSystem();
  }

  handleWindowFocus() {
    this.isWindowFocused = true;
    console.log('📱 Window gained focus - restoring full particle system');

    // ⚠️ NAVIGATION FIX: Ensure animation is running
    this.animationPaused = false;

    // Restart animation if it was stopped
    if (!this.animationId && !this.disposed) {
      this.animate();
      console.log('🎬 Restarted animation on window focus');
    }

    this.optimizeParticleSystem();
  }

  handleAppSwitched(data) {
    this.isWindowFocused = data.focused;
    console.log(`📱 App switched - focused: ${data.focused}`);
    this.optimizeParticleSystem();
  }

  // ⚠️ PARTICLE OPTIMIZATION: Dynamic particle system optimization
  optimizeParticleSystem() {
    // ⚠️ NAVIGATION FIX: Modified performance optimization - never pause animation
    const shouldOptimize = !this.isWindowVisible || !this.isWindowFocused;
    const newParticleCount = shouldOptimize ? this.reducedParticleCount : this.baseParticleCount;
    const newFPS = shouldOptimize ? this.reducedFPS : this.targetFPS;

    if (newParticleCount !== this.particleCount) {
      console.log(`🔧 Particle optimization: ${this.particleCount} → ${newParticleCount} particles`);
      this.particleCount = newParticleCount;
      this.recreateParticleSystem();
    }

    if (newFPS !== this.currentFPS) {
      console.log(`🔧 FPS optimization: ${this.currentFPS} → ${newFPS} FPS`);
      this.currentFPS = newFPS;
    }

    // ⚠️ NAVIGATION FIX: Never pause animation - always keep it running
    // This prevents the visualization from disappearing during navigation
    this.animationPaused = false;

    if (shouldOptimize) {
      console.log('🔋 Performance optimized - reduced particles/FPS but animation continues');
    } else {
      console.log('▶️ Full performance restored');
    }
  }

  // ⚠️ PARTICLE OPTIMIZATION: Efficient particle system recreation
  recreateParticleSystem() {
    if (!this.scene || !this.sceneGroup) return;

    // Remove old particles
    if (this.particles) {
      this.sceneGroup.remove(this.particles);

      // Dispose old resources
      if (this.particles.geometry) {
        this.particles.geometry.dispose();
      }
      if (this.particles.material) {
        this.particles.material.dispose();
      }
    }

    // Create new optimized particle system
    this.createParticles();
    console.log(`✅ Particle system recreated with ${this.particleCount} particles`);
  }

  async switchMicrophone(deviceId) {
    console.log(`🎤 Switching microphone for ${this.setupId}:`, deviceId);
    if (this.isBackgroundListening && !this.isSettingUpAudio) {
      this.selectedDeviceId = deviceId || 'default';
      await this.setupAudio(deviceId);
    }
  }

  setupDeviceSwitching() {
    // Clean up any existing device switch handlers
    this.cleanupDeviceSwitching();

    if (!this.autoDeviceSwitching) {
      return;
    }

    // Listen for device changes from DeviceManager
    const handleDeviceSwitch = (deviceInfo) => {
      if (this.disposed || !this.isBackgroundListening) {
        return;
      }

      console.log(`🔄 Device switched detected for ${this.setupId}:`, deviceInfo);

      // Only auto-switch if we're using system default
      if (this.selectedDeviceId === 'default') {
        this.handleAutomaticDeviceSwitch(deviceInfo);
      }
    };

    const handleDeviceChange = (eventData) => {
      if (this.disposed || !this.isBackgroundListening) {
        return;
      }

      console.log(`🔧 Device change detected for ${this.setupId}:`, eventData);

      // Refresh our audio setup if using default device
      if (this.selectedDeviceId === 'default') {
        // Small delay to let system settle
        setTimeout(() => {
          if (!this.disposed && this.isBackgroundListening) {
            this.refreshAudioForDeviceChange();
          }
        }, 200);
      }
    };

    // Add listeners to DeviceManager
    deviceManager.on('inputDeviceChanged', handleDeviceSwitch);
    deviceManager.on('deviceChange', handleDeviceChange);

    // Store cleanup functions
    this.deviceSwitchHandlers.push(
      () => deviceManager.removeListener('inputDeviceChanged', handleDeviceSwitch),
      () => deviceManager.removeListener('deviceChange', handleDeviceChange)
    );

    console.log(`🔧 Device switching setup completed for ${this.setupId}`);
  }

  async handleAutomaticDeviceSwitch(eventData) {
    try {
      console.log(`🔄 Handling automatic device switch for ${this.setupId}`);

      // Update our current device tracking
      this.currentDeviceId = 'default';

      // Refresh audio setup with new device
      await this.refreshAudioSetup();

    } catch (error) {
      console.error(`❌ Failed to handle automatic device switch for ${this.setupId}:`, error);
    }
  }

  async refreshAudioForDeviceChange() {
    try {
      console.log(`🔄 Refreshing audio setup due to device change for ${this.setupId}`);

      // Small delay to ensure globalAudioManager has processed the change
      await new Promise(resolve => setTimeout(resolve, 100));

      // Refresh audio setup
      await this.refreshAudioSetup();

    } catch (error) {
      console.error(`❌ Failed to refresh audio for device change for ${this.setupId}:`, error);
    }
  }

  async refreshAudioSetup() {
    if (this.disposed || !this.isBackgroundListening) {
      return;
    }

    try {
      // Disconnect current microphone source
      if (this.microphone) {
        this.microphone.disconnect();
        this.microphone = null;
      }

      // Get fresh stream from globalAudioManager
      const stream = globalAudioManager.microphoneStream;
      if (!stream || !stream.active) {
        console.warn(`⚠️ No active stream available for refresh for ${this.setupId}`);
        return;
      }

      // Update current stream reference
      this.currentStream = stream;
      const audioTrack = stream.getAudioTracks()[0];
      console.log(`🔄 Refreshed to microphone (${this.setupId}): ${audioTrack.label}`);

      // Trigger event to update UI with actual device being used
      window.dispatchEvent(new CustomEvent('actualMicrophoneChanged', {
        detail: {
          deviceId: audioTrack.getSettings().deviceId || 'default',
          label: audioTrack.label
        }
      }));

      // Reconnect audio source
      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.microphone.connect(this.analyser);

    } catch (error) {
      console.error(`❌ Failed to refresh audio setup for ${this.setupId}:`, error);
    }
  }

  cleanupDeviceSwitching() {
    // Call all device switch cleanup functions
    this.deviceSwitchHandlers.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.warn(`Warning: Device switch cleanup error for ${this.setupId}:`, error);
      }
    });
    this.deviceSwitchHandlers = [];
  }

  animate() {
    // ⚠️ MEMORY LEAK FIX: Proper requestAnimationFrame cleanup
    const animateLoop = (currentTime) => {
      // Check if disposed before requesting next frame
      if (this.disposed) {
        if (this.animationId) {
          cancelAnimationFrame(this.animationId);
          this.animationId = null;
        }
        console.log(`🛑 Animation stopped for disposed ${this.setupId}`);
        return;
      }

      // ⚠️ NAVIGATION FIX: Never pause animation for navigation
      // Always continue rendering to prevent disappearing visualization

      // ⚠️ PARTICLE OPTIMIZATION: FPS limiting for battery optimization
      const deltaTime = currentTime - this.lastFrameTime;
      const targetFrameTime = 1000 / this.currentFPS;

      if (deltaTime >= targetFrameTime) {
        this.lastFrameTime = currentTime;

        if (this.renderer && this.scene && this.camera) {
          this.updateParticles();
          this.renderer.render(this.scene, this.camera);
        }
      }

      // Store animation ID for proper cleanup
      this.animationId = requestAnimationFrame(animateLoop);
    };

    // Start the animation loop
    this.animationId = requestAnimationFrame(animateLoop);

    console.log(`🎨 Started persistent animation loop for ${this.setupId} with dynamic FPS control`);
  }

  dispose() {
    console.log(`🧹 Disposing ParticleVisualizer ${this.setupId}`);

    // ⚠️ MEMORY LEAK FIX: Mark as disposed FIRST to stop animation immediately
    this.disposed = true;

    // ⚠️ MEMORY LEAK FIX: Cancel animation frame to stop infinite loop
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
      console.log(`🎬 Animation frame cancelled for ${this.setupId}`);
    }

    // ⚠️ MEMORY LEAK FIX: Remove event listeners BEFORE disposing renderer
    this.removeEventListeners();

    // ⚠️ GLOBAL EVENT LISTENER FIX: Remove all global listeners
    this.removeGlobalEventListeners();

    // Stop current stream only if it's not the global one
    if (this.currentStream && this.currentStream !== globalAudioManager.microphoneStream) {
      this.currentStream.getTracks().forEach(track => track.stop());
    }
    this.currentStream = null;

    // Disconnect audio nodes
    if (this.microphone) {
      this.microphone.disconnect();
      this.microphone = null;
    }

    // Don't close the global audio context, just disconnect
    this.audioContext = null;
    this.analyser = null;

    // ⚠️ THREE.js COMPLETE RESOURCE DISPOSAL - FULLY RESOLVED
    this.disposeThreeJSResources();

    console.log(`✅ ParticleVisualizer ${this.setupId} disposed with COMPLETE THREE.js resource cleanup`);
  }

  // ⚠️ GLOBAL EVENT LISTENER FIX: Complete global listener cleanup
  removeGlobalEventListeners() {
    // Remove window and document listeners with bound functions
    if (this.boundMicrophoneChanged) {
      window.removeEventListener('microphoneChanged', this.boundMicrophoneChanged);
    }
    if (this.boundVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    }
    if (this.boundWindowBlur) {
      window.removeEventListener('blur', this.boundWindowBlur);
    }
    if (this.boundWindowFocus) {
      window.removeEventListener('focus', this.boundWindowFocus);
    }

    // Remove globalAudioManager listeners
    if (globalAudioManager && this.boundAppSwitched) {
      globalAudioManager.off('appSwitched', this.boundAppSwitched);
    }

    // Clear bound function references
    this.boundMicrophoneChanged = null;
    this.boundVisibilityChange = null;
    this.boundWindowBlur = null;
    this.boundWindowFocus = null;
    this.boundAppSwitched = null;

    console.log(`🧹 Global event listeners removed for ${this.setupId}`);
  }

  // ⚠️ COMPLETE THREE.js RESOURCE MANAGEMENT - FULLY RESOLVED
  disposeThreeJSResources() {
    console.log(`🧹 Starting COMPLETE THREE.js resource disposal for ${this.setupId}`);

    // 1. ⚠️ TEXTURE DISPOSAL (was missing)
    console.log(`📎 Disposing ${this.threejsResources.textures.size} textures`);
    this.threejsResources.textures.forEach(texture => {
      if (texture && typeof texture.dispose === 'function') {
        texture.dispose();
      }
    });
    this.threejsResources.textures.clear();

    // 2. ⚠️ MATERIAL DISPOSAL (enhanced with texture cleanup)
    console.log(`📎 Disposing ${this.threejsResources.materials.size} materials`);
    this.threejsResources.materials.forEach(material => {
      if (material && typeof material.dispose === 'function') {
        // Dispose material's embedded textures
        if (material.map) material.map.dispose();
        if (material.lightMap) material.lightMap.dispose();
        if (material.bumpMap) material.bumpMap.dispose();
        if (material.normalMap) material.normalMap.dispose();
        if (material.specularMap) material.specularMap.dispose();
        if (material.envMap) material.envMap.dispose();
        if (material.alphaMap) material.alphaMap.dispose();
        if (material.aoMap) material.aoMap.dispose();
        if (material.displacementMap) material.displacementMap.dispose();
        if (material.emissiveMap) material.emissiveMap.dispose();
        if (material.gradientMap) material.gradientMap.dispose();
        if (material.metalnessMap) material.metalnessMap.dispose();
        if (material.roughnessMap) material.roughnessMap.dispose();

        material.dispose();
      }
    });
    this.threejsResources.materials.clear();

    // 3. ⚠️ BUFFER ATTRIBUTE DISPOSAL (was missing)
    console.log(`📎 Disposing ${this.threejsResources.bufferAttributes.size} buffer attributes`);
    this.threejsResources.bufferAttributes.forEach(attribute => {
      if (attribute && attribute.array) {
        // Clear typed array data
        if (attribute.array.fill) {
          attribute.array.fill(0);
        }
        attribute.array = null;
      }
    });
    this.threejsResources.bufferAttributes.clear();

    // 4. ⚠️ GEOMETRY DISPOSAL (enhanced)
    console.log(`📎 Disposing ${this.threejsResources.geometries.size} geometries`);
    this.threejsResources.geometries.forEach(geometry => {
      if (geometry && typeof geometry.dispose === 'function') {
        // Dispose all attributes
        const attributes = geometry.attributes;
        for (const key in attributes) {
          if (attributes[key] && attributes[key].array) {
            attributes[key].array = null;
          }
        }
        // Dispose index if present
        if (geometry.index && geometry.index.array) {
          geometry.index.array = null;
        }
        geometry.dispose();
      }
    });
    this.threejsResources.geometries.clear();

    // 5. ⚠️ SCENE TRAVERSAL AND CLEANUP (enhanced)
    if (this.scene) {
      console.log(`📎 Traversing and cleaning scene objects`);
      this.scene.traverse((object) => {
        // Dispose geometry
        if (object.geometry && typeof object.geometry.dispose === 'function') {
          object.geometry.dispose();
        }

        // Dispose material(s)
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => {
              if (material && typeof material.dispose === 'function') {
                material.dispose();
              }
            });
          } else if (typeof object.material.dispose === 'function') {
            object.material.dispose();
          }
        }

        // Clear children references
        if (object.children) {
          object.children.length = 0;
        }
      });

      this.scene.clear();
      this.scene = null;
    }

    // 6. ⚠️ RENDERER DISPOSAL (enhanced with comprehensive cleanup)
    if (this.renderer) {
      console.log(`📎 Disposing WebGL renderer with complete context cleanup`);

      // Remove from DOM first
      if (this.container && this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }

      // Clear all render targets
      this.renderer.setRenderTarget(null);

      // Clear WebGL state
      const gl = this.renderer.getContext();
      if (gl) {
        // Clear all WebGL buffers
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);

        // Force context loss for complete cleanup
        const loseContext = gl.getExtension('WEBGL_lose_context');
        if (loseContext) {
          loseContext.loseContext();
        }
      }

      // Dispose renderer
      this.renderer.dispose();
      this.renderer = null;
    }

    // 7. ⚠️ CLEAR ALL OBJECT REFERENCES
    this.particles = null;
    this.camera = null;
    this.sceneGroup = null;
    this.clock = null;
    this.mouse = null;
    this.mouse3D = null;

    // 8. ⚠️ CLEAR ARRAYS TO FREE MEMORY
    this.particlePositions = null;
    this.particleVelocities = null;
    this.particleOpacities = null;
    this.particleStates = null;
    this.particleBasePositions = null;
    this.originalPositions = null;
    this.randomDirections = null;
    this.colors = null;
    this.dataArray = null;
    this.frequencyData = null;

    console.log(`✅ COMPLETE THREE.js resource disposal finished for ${this.setupId}`);
  }

  removeEventListeners() {
    // ⚠️ SAFETY CHECK: Only remove listeners if renderer and domElement exist and bound functions exist
    if (!this.renderer || !this.renderer.domElement) {
      return;
    }

    const canvas = this.renderer.domElement;

    // Remove mouse events with bound functions
    if (this.boundMouseDown) canvas.removeEventListener('mousedown', this.boundMouseDown);
    if (this.boundMouseUp) canvas.removeEventListener('mouseup', this.boundMouseUp);
    if (this.boundMouseMove) canvas.removeEventListener('mousemove', this.boundMouseMove);

    // Remove touch events with bound functions
    if (this.boundTouchStart) canvas.removeEventListener('touchstart', this.boundTouchStart);
    if (this.boundTouchMove) canvas.removeEventListener('touchmove', this.boundTouchMove);
    if (this.boundTouchEnd) canvas.removeEventListener('touchend', this.boundTouchEnd);

    // Remove window events with bound functions
    if (this.boundWindowResize) window.removeEventListener('resize', this.boundWindowResize);

    // Clear bound function references
    this.boundMouseDown = null;
    this.boundMouseUp = null;
    this.boundMouseMove = null;
    this.boundTouchStart = null;
    this.boundTouchMove = null;
    this.boundTouchEnd = null;
    this.boundWindowResize = null;
  }
}

// Global persistent visualizer instance to maintain state across navigation
let globalVisualizerInstance = null;
let globalVisualizerContainer = null;

// Global cleanup function
window.cleanupGlobalVisualizer = () => {
  if (globalVisualizerInstance) {
    console.log('🧹 Cleaning up global ParticleVisualizer instance');
    globalVisualizerInstance.dispose();
    globalVisualizerInstance = null;
    globalVisualizerContainer = null;
  }
};

const VoiceVisualizer = ({
  isBackgroundListening = false,
  onEnableListening,
  platformConfig = null,
  isMobile = false,
  showButton = false,
  voiceData, // Add voiceData prop
  // New props for query functionality
  onQueryStart,
  onQueryStop,
  isQueryMode = false,
  queryStatus = 'idle', // idle, recording, processing, transcribed, response
  queryAudioStream, // Audio stream for query mode visualization
  transcribedText = '', // Transcribed text to display
  llmResponse = '' // LLM response to display
}) => {
  const containerRef = useRef(null);
  const visualizerRef = useRef(null);
  const textOverlayRef = useRef(null);
  const userHasScrolledUp = useRef(false);
  const [statusText, setStatusText] = useState('Listening Mode');
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  // ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Use platform config or defaults
  const config = platformConfig || {
    particleCount: 1800,
    renderScale: 1.0,
    updateFrequency: 60,
    buttonDelay: 150,
    useTouch: false,
    lowPower: false
  };

  // Auto-scroll functionality for streaming response
  useEffect(() => {
    const overlay = textOverlayRef.current;
    if (!overlay) return;

    // Check if user is near bottom (within 100px)
    const isNearBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = overlay;
      return scrollHeight - scrollTop - clientHeight < 100;
    };

    // Handle scroll event - detect if user manually scrolled up
    const handleScroll = () => {
      if (isNearBottom()) {
        userHasScrolledUp.current = false; // User scrolled back to bottom
      } else {
        userHasScrolledUp.current = true; // User scrolled up
      }
    };

    // Auto-scroll to bottom if user hasn't scrolled up
    const scrollToBottom = () => {
      if (!userHasScrolledUp.current && queryStatus === 'response') {
        overlay.scrollTop = overlay.scrollHeight;
      }
    };

    overlay.addEventListener('scroll', handleScroll);
    scrollToBottom(); // Scroll when content changes

    return () => {
      overlay.removeEventListener('scroll', handleScroll);
    };
  }, [llmResponse, queryStatus]);

  // Reset scroll state when starting new response
  useEffect(() => {
    if (queryStatus === 'response' && llmResponse) {
      userHasScrolledUp.current = false; // Reset for new response
    }
  }, [queryStatus]);

  useEffect(() => {
    // Initialize the visualizer when we have a container
    if (containerRef.current) {
      if (!globalVisualizerInstance) {
        console.log('🎨 Creating new global ParticleVisualizer instance');
        globalVisualizerInstance = new ParticleVisualizer(
          containerRef.current,
          isBackgroundListening,
          onEnableListening,
          config // ⚠️ UNIVERSAL DEVICE COMPATIBILITY: Pass platform configuration
        );
        globalVisualizerContainer = containerRef.current;
        visualizerRef.current = globalVisualizerInstance;
      } else {
        console.log('🔄 Reusing existing global ParticleVisualizer instance');

        // ⚠️ NAVIGATION FIX: Properly transfer visualizer to new container
        if (globalVisualizerContainer !== containerRef.current) {
          // Remove from old container
          if (globalVisualizerInstance.renderer && globalVisualizerInstance.renderer.domElement) {
            const canvas = globalVisualizerInstance.renderer.domElement;
            if (canvas.parentNode) {
              console.log('📱 Moving canvas to new container');
              canvas.parentNode.removeChild(canvas);
            }
            // Add to new container
            containerRef.current.appendChild(canvas);

            // ⚠️ NAVIGATION FIX: Ensure proper resizing after container move
            setTimeout(() => {
              if (globalVisualizerInstance && !globalVisualizerInstance.disposed) {
                globalVisualizerInstance.onWindowResize();
                console.log('🔧 Resized visualizer after container move');
              }
            }, 100);
          }
          globalVisualizerContainer = containerRef.current;
        }

        visualizerRef.current = globalVisualizerInstance;

        // ⚠️ NAVIGATION FIX: Update container reference and restart animation if needed
        if (globalVisualizerInstance.container !== containerRef.current) {
          globalVisualizerInstance.container = containerRef.current;
        }

        // ⚠️ NAVIGATION FIX: Ensure animation is running after navigation
        if (globalVisualizerInstance.animationPaused || !globalVisualizerInstance.animationId) {
          globalVisualizerInstance.animationPaused = false;
          if (!globalVisualizerInstance.animationId) {
            globalVisualizerInstance.animate();
            console.log('🎬 Restarted animation after navigation');
          }
        }
      }

      // Update background listening state
      if (visualizerRef.current) {
        visualizerRef.current.updateBackgroundListening(isBackgroundListening);
      }
    }

    // ⚠️ NAVIGATION FIX: Force cleanup only when app is truly closing
    return () => {
      // Don't dispose the global instance on unmount - keep it alive for navigation reuse
      console.log('🔄 Dashboard unmounting - keeping visualizer alive for reuse');
    };
  }, [isBackgroundListening, onEnableListening, config]); // Remove isQueryMode from dependencies

  useEffect(() => {
    if (visualizerRef.current) {
      // When switching from query mode back to listening mode, ensure audio processing is restarted
      if (isQueryMode === false && isBackgroundListening) {
        console.log('🔄 Restarting audio processing after query mode');
        // Ensure the visualizer's background listening is enabled
        visualizerRef.current.updateBackgroundListening(true);

        // Give a small delay to ensure the visualizer is properly set up
        setTimeout(() => {
          if (visualizerRef.current && !visualizerRef.current.audioEnabled) {
            console.log('🔄 Forcing audio processing restart');
            visualizerRef.current.updateBackgroundListening(true);
          }
        }, 100);
      }

      // Update status text
      if (!isQueryMode) {
        setStatusText(isBackgroundListening ? 'Listening Mode' : 'Voice Disabled');
      }
    }
  }, [isBackgroundListening, isQueryMode]);

  // Handle window state changes to maintain visualization during minimize/tray
  useEffect(() => {
    const handleWindowStateChange = (state) => {
      if (visualizerRef.current) {
        // ⚠️ NAVIGATION FIX: Never pause animation during navigation or minimize
        // Always keep the visualizer running to prevent disappearing during navigation
        console.log('🔄 Window state changed:', state, '- keeping animation active');

        // Force resume animation regardless of window state
        if (visualizerRef.current.animationPaused) {
          visualizerRef.current.animationPaused = false;
          console.log('▶️ Force resuming animation for navigation');
        }

        // Ensure animation loop is running
        if (!visualizerRef.current.animationId) {
          visualizerRef.current.animate();
          console.log('🎬 Restarting animation loop for navigation');
        }
      }
    };

    // Listen for query mode ended event
    const handleQueryModeEnded = () => {
      if (visualizerRef.current && isBackgroundListening && !isQueryMode) {
        console.log('🔄 Query mode ended, restarting audio processing');
        // Ensure the visualizer's background listening is enabled
        visualizerRef.current.updateBackgroundListening(true);

        // Give a small delay to ensure the visualizer is properly set up
        setTimeout(() => {
          if (visualizerRef.current && !visualizerRef.current.audioEnabled) {
            visualizerRef.current.updateBackgroundListening(true);
          }
        }, 100);
      }
    };

    // Listen for window state changes from Electron
    if (window.electronAPI && window.electronAPI.onWindowStateChange) {
      const cleanupWindowState = window.electronAPI.onWindowStateChange(handleWindowStateChange);

      // Listen for query mode ended event
      window.addEventListener('queryModeEnded', handleQueryModeEnded);

      return () => {
        cleanupWindowState();
        window.removeEventListener('queryModeEnded', handleQueryModeEnded);
      };
    } else {
      // Listen for query mode ended event
      window.addEventListener('queryModeEnded', handleQueryModeEnded);

      return () => {
        window.removeEventListener('queryModeEnded', handleQueryModeEnded);
      };
    }
  }, [isBackgroundListening, isQueryMode]);

  // Update visualizer with external voice data for continuous visualization
  useEffect(() => {
    if (visualizerRef.current && voiceData) {
      // Inject global voice data into the visualizer for continuous display
      // This ensures visualization state is preserved even when window is minimized
      if (voiceData.volume !== undefined) {
        visualizerRef.current.audioLevel = voiceData.volume;
        visualizerRef.current.smoothedAudioLevel = visualizerRef.current.smoothedAudioLevel * 0.7 + voiceData.volume * 0.3;
      }
      if (voiceData.isActive !== undefined) {
        visualizerRef.current.isVoiceActive = voiceData.isActive;
        visualizerRef.current.voiceActivityLevel = voiceData.volume || 0;
      }
      if (voiceData.frequency && visualizerRef.current.dataArray && voiceData.frequency.length > 0) {
        // Update frequency data if available
        const minLength = Math.min(voiceData.frequency.length, visualizerRef.current.dataArray.length);
        for (let i = 0; i < minLength; i++) {
          visualizerRef.current.dataArray[i] = voiceData.frequency[i];
        }
      }

      // Ensure animation continues even when window is not visible
      if (globalVisualizerInstance && globalVisualizerInstance.renderer) {
        // Keep the animation loop running
        globalVisualizerInstance.renderer.setAnimationLoop(() => {
          if (globalVisualizerInstance) {
            globalVisualizerInstance.updateParticles();
            globalVisualizerInstance.renderer.render(
              globalVisualizerInstance.scene,
              globalVisualizerInstance.camera
            );
          }
        });
      }
    }
  }, [voiceData]);

  useEffect(() => {
    // Update status text based on query mode and background listening state
    if (isQueryMode) {
      switch (queryStatus) {
        case 'recording':
          setStatusText('Query Mode');
          break;
        case 'processing':
          setStatusText('Processing Query');
          break;
        case 'transcribed':
          setStatusText('Processing Query');
          break;
        case 'response':
          setStatusText('Response Ready');
          break;
        default:
          setStatusText('Query Mode');
      }
    } else {
      setStatusText(isBackgroundListening ? 'Listening Mode' : 'Voice Disabled');
    }
  }, [isBackgroundListening, isQueryMode, queryStatus]);

  const handleEnableListening = () => {
    if (onEnableListening) {
      onEnableListening();
    }
  };

  // Handler for the Ask Anything button
  const handleQueryAction = () => {
    if (isQueryMode && queryStatus === 'recording') {
      // Stop recording
      if (onQueryStop) {
        onQueryStop();
      }
    } else {
      // Start query mode
      if (onQueryStart) {
        onQueryStart();
      }
    }
  };

  return (
    <VisualizerContainer>
      {/* Always render CanvasWrapper but control visibility with CSS */}
      <CanvasWrapper
        ref={containerRef}
        style={{ display: isQueryMode ? 'none' : 'flex' }}
        animate={{
          scale: (queryStatus === 'transcribed') ? 0.45 : 1,
          y: (queryStatus === 'transcribed') ? -100 : 0,
          opacity: (queryStatus === 'response') ? 0 : 1
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
          duration: 0.3
        }}
      />

      {/* Show 2D waveform visualization when in query mode */}
      {isQueryMode && (
        <QueryVisualizerWrapper
          animate={{
            scale: (queryStatus === 'transcribed') ? 0.45 : 1,
            y: (queryStatus === 'transcribed') ? -100 : 0,
            opacity: (queryStatus === 'response') ? 0 : 1
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 20,
            duration: 0.3
          }}
        >
          <QueryVisualizer
            audioStream={queryAudioStream}
            isProcessing={queryStatus === 'processing' || queryStatus === 'transcribed'}
          />
        </QueryVisualizerWrapper>
      )}

      {/* Show transcribed text when in transcribed or response state */}
      <AnimatePresence>
        {isQueryMode && (queryStatus === 'transcribed' || queryStatus === 'response') && (transcribedText || llmResponse) && (
          <TranscribedTextOverlay
            ref={textOverlayRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {queryStatus === 'response' ? (
              <ReactMarkdown>{llmResponse || transcribedText}</ReactMarkdown>
            ) : (
              transcribedText
            )}
          </TranscribedTextOverlay>
        )}
      </AnimatePresence>

      <StatusLabel
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {statusText}
      </StatusLabel>

      <AnimatePresence>
        {showButton && !isQueryMode && (
          <CenterButton
            key="enable-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: isMobile ? 0.4 : 0.3,
              ease: "easeOut"
            }}
            onClick={handleEnableListening}
            style={{
              fontSize: isMobile ? '0.9rem' : '1rem',
              padding: isMobile ? '12px 24px' : '16px 32px',
              minHeight: isMobile ? '48px' : 'auto',
              touchAction: 'manipulation',
              userSelect: 'none'
            }}
          >
            Enable Background Listening
          </CenterButton>
        )}
      </AnimatePresence>

      {/* Ask Anything Button for Query Mode */}
      <AnimatePresence>
        {!showButton && !isQueryMode && (
          <QueryButton
            key="ask-anything-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: isMobile ? 0.4 : 0.3,
              ease: "easeOut"
            }}
            onClick={handleQueryAction}
            style={{
              fontSize: isMobile ? '0.9rem' : '1rem',
              padding: isMobile ? '12px 24px' : '16px 32px',
              minHeight: isMobile ? '48px' : 'auto',
              touchAction: 'manipulation',
              userSelect: 'none'
            }}
          >
            <Mic size={16} />
            Ask Anything
          </QueryButton>
        )}
      </AnimatePresence>

      {/* Stop Button for Recording Mode */}
      <AnimatePresence>
        {isQueryMode && queryStatus === 'recording' && (
          <QueryButton
            key="stop-button"
            className="recording"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: isMobile ? 0.4 : 0.3,
              ease: "easeOut"
            }}
            onClick={handleQueryAction}
            style={{
              fontSize: isMobile ? '0.9rem' : '1rem',
              padding: isMobile ? '12px 24px' : '16px 32px',
              minHeight: isMobile ? '48px' : 'auto',
              touchAction: 'manipulation',
              userSelect: 'none'
            }}
          >
            <Square size={16} />
            Stop Recording
          </QueryButton>
        )}
      </AnimatePresence>
    </VisualizerContainer>
  );
};

export default VoiceVisualizer;

